import { isObject } from "common/utils/core";

const APP_MONITORING_KEY = "__APP_MONITORING__";

type NotifyOptions = {
  context?: Record<string, unknown>;
  params?: Record<string, unknown>;
  tags?: string;
  [key: string]: unknown;
};

const getFlags = (): { datadogEnabled: boolean } => {
  const flags = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { flags?: { datadogEnabled?: boolean } }
    | undefined;
  return {
    datadogEnabled: flags?.flags?.datadogEnabled !== false
  };
};

const resolveContext = (options: NotifyOptions | undefined): Record<string, unknown> => {
  if (!options || !isObject(options)) return {};
  if (isObject(options.context)) {
    const { context, params, ...rest } = options;
    return {
      ...(context as Record<string, unknown>),
      ...(isObject(params) ? (params as Record<string, unknown>) : {}),
      ...rest
    };
  }
  return options as Record<string, unknown>;
};

const mergeGlobalContext = (callSiteContext: Record<string, unknown>): Record<string, unknown> => {
  const appMonitoring = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { context?: Record<string, unknown>; user?: Record<string, unknown> }
    | undefined;
  const globalContext = appMonitoring?.context || {};
  const globalUser = appMonitoring?.user || {};
  return { ...globalContext, ...globalUser, ...callSiteContext };
};

const canSendError = (): boolean => {
  const mon = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { notifyErrorCount?: number; flags?: { maxErrorsPerPage?: number } }
    | undefined;
  if (!mon) return true;
  const raw = Number(mon.flags?.maxErrorsPerPage);
  const max = Number.isFinite(raw) && raw > 0 ? raw : 20;
  const monRecord = mon as Record<string, unknown>;
  const count = (monRecord["notifyErrorCount"] as number) || 0;
  if (count >= max) return false;
  monRecord["notifyErrorCount"] = count + 1;
  return true;
};

const reportToDatadog = (messageOrError: string | Error, options?: NotifyOptions): void => {
  try {
    if (!window.DD_RUM || typeof window.DD_RUM.addError !== "function") return;
    if (!canSendError()) return;

    const callSiteContext = resolveContext(options);
    const mergedContext = mergeGlobalContext(callSiteContext);

    if (messageOrError instanceof Error) {
      window.DD_RUM.addError(messageOrError, mergedContext);
    } else {
      const error = new Error(messageOrError);
      error.name = "CustomError";
      window.DD_RUM.addError(error, mergedContext);
    }
  } catch (e) {
    console.error(e);
  }
};

export function notifyError(messageOrError: string | Error, options?: NotifyOptions): void {
  const { datadogEnabled } = getFlags();

  if (datadogEnabled) {
    reportToDatadog(messageOrError, options);
  }
}

// Expose notifyError for legacy Sprockets-bundled scripts that cannot use ES module imports.
// These scripts access it via window.__APP_MONITORING__.notifyError?.().
if (!(window as unknown as Record<string, unknown>)[APP_MONITORING_KEY]) {
  (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] = {};
}
((window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as Record<string, unknown>)[
  "notifyError"
] = notifyError;

// Patch queueError to delegate directly now that notifyError is available.
try {
  const monObj = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as Record<
    string,
    unknown
  >;
  monObj["queueError"] = notifyError;
} catch (e) {
  console.error(e);
}

// Flush errors queued before the monitoring bundle loaded.
// Must be called AFTER DD_RUM is initialized (from datadog.js), otherwise
// reportToDatadog bails on the !window.DD_RUM guard and errors are lost.
export function flushPendingErrors(): void {
  try {
    const mon = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
      | { pendingErrors?: [string | Error, NotifyOptions?][] }
      | undefined;
    const pending = mon?.pendingErrors;
    if (Array.isArray(pending) && pending.length > 0) {
      const batch = pending.splice(0, pending.length);
      batch.forEach(([msg, opts]) => notifyError(msg, opts));
    }
  } catch (e) {
    console.error(e);
  }
}
